/**
 * `appSettingsDto.ts` — wire shape of `app-settings.json`, the deployment's
 * SHARED application settings.
 *
 * One deployment-scoped document served by the web host at
 * `/__campfire_config/{deployment}/settings`. Everyone using that deployment
 * reads the same document, so a setting flipped by one person is the setting
 * everyone gets.
 *
 * **Web-only — there is no Unity counterpart.** The desktop client has no
 * runtime settings document at all: what it gates, it gates at build time
 * through `IAppConfig`. Keys are therefore camelCase, unlike the PascalCase
 * `docs/index.json` (which is shaped by Unity's Newtonsoft defaults because
 * Unity reads it).
 *
 * ## Every map is SPARSE, and that is load-bearing
 *
 * An absent key means "the stock default", never "false":
 *
 *   - `skills` / `roles` only record entries a developer has explicitly turned
 *     OFF, so one added to `public/AI/{skills,roles}/index.json` is available the
 *     moment it ships, without anyone having to touch this document — and one
 *     removed from the manifest leaves at most a harmless stale key behind.
 *   - `devSettings` only records values that DIFFER from what an ungated build
 *     does (see `K_DEV_SETTING_DEFAULTS`), which keeps the same safety property
 *     the old `localStorage` mirror had: a document that fails to load, or has
 *     not been written yet, reads as stock behaviour rather than as everything
 *     switched off.
 *
 * Unknown keys pass through `[k: string]: unknown` and are preserved across a
 * read-modify-write, the same rule `docsManifestDto` / `assetDtoToItem` follow:
 * a newer build's fields must survive an older build saving a toggle.
 */

/** Customer-scoped storage path, joined under `account/{customerId}/projectdata/`. */
export const APP_SETTINGS_STORAGE_PATH = "settings/app-settings.json";

/** Bumped only for a breaking reshape; readers tolerate an unknown value. */
export const APP_SETTINGS_SCHEMA_VERSION = 1;

/**
 * The dev-panel overrides, by key. Every one is optional — see the sparse-map
 * note above. Mirrors `IDevSettingsModel`, which is the only reader.
 */
export interface AppSettingsDevSettingsDto {
  userFacingWorkflowAuthoring?: boolean;
  respondWithVoice?: boolean;
  hideSparkLabel?: boolean;
  collapsibleSparkRow?: boolean;
  superGizmo?: boolean;
  webRtcRealtimeTransport?: boolean;
  [k: string]: unknown;
}

/**
 * Every reasoning-effort value this client will STORE and round-trip. `"none"`
 * is a real value (the Unity helper default), distinct from ABSENT — absent
 * means "send no `reasoning` key at all".
 *
 * ⚠ This is the storage vocabulary, **not** what any one endpoint accepts —
 * see {@link reasoningEffortChoicesForModel}. It has to be the UNION, because
 * `sanitizeRoleModels` DELETES an unrecognised effort on parse: a value missing
 * from here saves, survives until the next document read, then silently
 * reverts to the role's default with nothing reported.
 */
export const AI_REASONING_EFFORTS = [
  "none",
  "minimal",
  "low",
  "medium",
  "high",
  "xhigh",
] as const;
export type AIReasoningEffort = (typeof AI_REASONING_EFFORTS)[number];

export function isReasoningEffort(value: unknown): value is AIReasoningEffort {
  return (
    typeof value === "string" && (AI_REASONING_EFFORTS as readonly string[]).includes(value)
  );
}

/**
 * The efforts a given model's endpoint actually accepts — **the two targets do
 * not agree, which is why this is not one flat list.** Measured 2026-08-28:
 *
 * | effort    | realtime 2.1 / 2.1-mini | Responses gpt-5.6-sol |
 * | --------- | ----------------------- | --------------------- |
 * | `minimal` | accepted                | **refused** (`unsupported_value`) |
 * | others    | accepted                | accepted              |
 *
 * So `minimal` is offered only for a realtime-family model. That asymmetry is
 * not cosmetic in either direction: an effort the REALTIME endpoint refuses
 * voids the whole `session.update`, so the instructions and every gated tool
 * schema silently fail to apply (the symptom is "Spark is being odd", not an
 * error); an effort the RESPONSES endpoint refuses 400s every bridged turn.
 * `minimal` was in the shipped list before this table existed, so a
 * workflow-builder role set to it would have failed exactly that way.
 *
 * Note this takes the MODEL, not a transport: which endpoint answers is a
 * property of the model id, and `AIOrchestratorModel.resolveSessionConnection`
 * routes a non-realtime model to the text bridge on the same test.
 */
export function reasoningEffortChoicesForModel(
  model: string | null,
): ReadonlyArray<AIReasoningEffort> {
  // `null` / blank is what an ABSENT `roleModels.model` means — the stock
  // realtime model — so it takes the realtime vocabulary. Reading it as
  // "not realtime" would hide `minimal` from exactly the roles that can use it,
  // and it is the reading the nullable signature invites.
  const trimmed = (model ?? "").trim().toLowerCase();
  const isRealtime = trimmed === "" || trimmed.startsWith("gpt-realtime");
  return isRealtime
    ? AI_REASONING_EFFORTS
    : AI_REASONING_EFFORTS.filter((effort) => effort !== "minimal");
}

/**
 * One role's session-model override, stored under `roleModels` (role id →
 * entry). Both fields optional and independently sparse: an absent field means
 * that role's stock default from {@link K_ROLE_MODEL_DEFAULTS}. `model` is
 * deliberately an open string (a hand-edited document may name a model newer
 * than this build's choice list); `reasoningEffort` is validated against
 * {@link AI_REASONING_EFFORTS} on read, so a malformed value falls back to the
 * default instead of reaching the wire.
 */
export interface AppRoleModelDto {
  model?: string;
  reasoningEffort?: string;
  [k: string]: unknown;
}

/**
 * A role's EFFECTIVE session-model config (stored override merged over the
 * role's stock default). `model: null` = the stock realtime session model;
 * `reasoningEffort: null` = send no `reasoning` key.
 */
export interface RoleModelConfig {
  readonly model: string | null;
  readonly reasoningEffort: AIReasoningEffort | null;
}

/**
 * What each role runs on when the document is silent. Only roles listed here
 * deviate from the stock realtime model — everything absent resolves to
 * `{ model: null, reasoningEffort: null }`.
 *
 * `workflow-builder` ships on gpt-5.6-sol at medium effort: workflow
 * preparation/validation is deliberate, structured work where reasoning
 * quality beats voice latency. A non-realtime model is served by the
 * orchestrator's Responses text bridge (the realtime endpoint refuses such
 * models at connect — measured 2026-08-13); a `gpt-realtime-*` value rides
 * the websocket itself.
 */
/**
 * The reasoning effort a REALTIME-family model runs at when the account has
 * configured none — i.e. every voice role, out of the box.
 *
 * Realtime 2.x is the first realtime model to accept `reasoning.effort` at all
 * (1.5 refuses it with `invalid_value`, measured 2026-08-28), so before that
 * this could only have been "send no key". OpenAI's own default is `low` and
 * their guidance recommends it for latency; `high` is a deliberate Campfire
 * choice — Spark's turns are tool-heavy and multi-step, where deliberation is
 * worth more than the response starting a beat sooner.
 *
 * ⚠ Applied in `AIOrchestratorModel.resolveSessionConnection`, NOT here, and
 * only when the resolved model is realtime-family: a Responses-family role goes
 * through the text bridge and takes its own default from
 * {@link K_ROLE_MODEL_DEFAULTS}. An explicit stored effort always wins, so
 * `none` remains the way to ask for no deliberation.
 */
export const K_DEFAULT_REALTIME_REASONING_EFFORT: AIReasoningEffort = "high";

/**
 * The effort a session should actually send, given the resolved model and
 * whatever the account configured (`null` = configured nothing).
 *
 * Only a REALTIME-family model takes the default: a Responses-family role runs
 * through the text bridge and takes its own from {@link K_ROLE_MODEL_DEFAULTS},
 * so defaulting it here would silently override that. An explicit stored value
 * always wins — including `"none"`, which is a real value meaning "do not
 * deliberate", distinct from having configured nothing.
 */
export function resolveRealtimeReasoningEffort(
  model: string,
  configured: AIReasoningEffort | null,
): AIReasoningEffort | null {
  if (configured !== null) return configured;
  const isRealtime = model.trim().toLowerCase().startsWith("gpt-realtime");
  return isRealtime ? K_DEFAULT_REALTIME_REASONING_EFFORT : null;
}

export const K_ROLE_MODEL_DEFAULTS: Readonly<Record<string, RoleModelConfig>> = {
  "workflow-builder": { model: "gpt-5.6-sol", reasoningEffort: "medium" },
};

/**
 * The models the debug panel's dropdown offers. A curated list, not a
 * validation rule — `AppRoleModelDto.model` accepts any non-empty string, so
 * a stored value outside this list still round-trips and applies. The
 * non-realtime entries run via the text bridge; the `gpt-realtime-*` entries
 * are the ones the realtime endpoint accepts (1.5 / mini measured 2026-08-13,
 * 2.1 / 2.1-mini measured 2026-08-28).
 *
 * ⚠ Entries must be LOWERCASE: `normalizeRoleModelId` canonicalizes by
 * `find((id) => id === lower)`, so a mixed-case entry could never match itself.
 *
 * `gpt-realtime-1.5` is kept although the stock model moved to 2.1. Two
 * reasons, and the first is the load-bearing one: the panel renders
 * `config.model ?? ""` against this fixed option list, so an account that has
 * 1.5 stored could not even SEE what it is set to if the entry were dropped.
 * It is also the only A/B for the reasoning-effort self-heal — 1.5 refuses
 * `reasoning.effort` and 2.1 accepts it.
 */
export const K_ROLE_MODEL_CHOICES: ReadonlyArray<string> = [
  "gpt-5.6-sol",
  "gpt-5.6-luna",
  "gpt-5.6-terra",
  "gpt-5.5",
  "gpt-realtime-2.1",
  "gpt-realtime-2.1-mini",
  "gpt-realtime-1.5",
  "gpt-realtime-mini",
];

/**
 * Canonicalizes model ids this client knows while preserving newer custom ids
 * verbatim. OpenAI model ids are case-sensitive. The first implementation
 * shipped `gpt-5.6-Sol` and the nonexistent generic `gpt-5.6`; both migrate to
 * the API's exact `gpt-5.6-sol` id on read/write.
 */
export function normalizeRoleModelId(value: unknown): string | null {
  if (typeof value !== "string" || !value.trim()) return null;
  const trimmed = value.trim();
  const lower = trimmed.toLowerCase();
  const known = K_ROLE_MODEL_CHOICES.find((id) => id === lower);
  if (known) return known;
  if (lower === "gpt-5.6") return "gpt-5.6-sol";
  return trimmed;
}

export interface AppSettingsDto {
  version?: number;
  /** ISO-8601 UTC stamp of the last successful write, for the panel's status line. */
  modifiedAt?: string;
  /** Display name of whoever last wrote the document, same purpose. */
  modifiedBy?: string;
  /**
   * Skill id → enabled. Sparse: only explicit `false` entries are meaningful,
   * everything absent is enabled. Ids are the normalized ones
   * (`normalizeSkillId`), so they match `AIRealtimeSkillDefinition.id`.
   */
  skills?: Record<string, boolean>;
  /**
   * Role id → enabled, same sparse rule as `skills`. Ids match
   * `AIRealtimeRoleDefinition.id` (lowercased `/AI/roles/index.json` ids).
   *
   * A `false` here is enforced by `AIInterfaceModel`, not by this document, and
   * one entry can never take effect: the DEFAULT role is the fallback the whole
   * role system resolves to (`DEFAULT_ROLE_ID`), so the gate keeps it enabled
   * whatever is stored — otherwise a hand-edited document could leave the agent
   * with no persona and no role to switch to.
   */
  roles?: Record<string, boolean>;
  /**
   * Role id → session-model override. Sparse like everything else: absent
   * means the role's `K_ROLE_MODEL_DEFAULTS` entry (or the stock realtime
   * model). Consumed by the orchestrator's session policy; written by the
   * debug panel's model/effort dropdowns. NOT debug-gated — like the
   * `skills`/`roles` gates, this configures what the product does for the
   * whole account, so it must not flip when a developer leaves debug mode.
   */
  roleModels?: Record<string, AppRoleModelDto>;
  /**
   * Role id → skill id → whether that skill is in the role's scope. Two sparse
   * levels: an absent role entry, and an absent skill inside one, both mean
   * "whatever the skill's own file declares".
   *
   * **Why this is stored at all, rather than edited in the skill file.** A
   * skill's role scope is `roles:` — and for skills the MANIFEST wins over the
   * file's frontmatter (`AIRealtimeSkillCatalog`: `manifestRoles.length > 0 ?
   * manifestRoles : splitList(frontmatterRoles)`), every shipped entry sets it,
   * and the manifest is deliberately not publishable. So an edit to the file
   * would be a silent no-op, and this document is the only place the scope can
   * be changed at runtime.
   *
   * Unlike the flat gates above, an entry here is meaningful in BOTH
   * directions: `false` withdraws a skill from a role its file grants, `true`
   * adds one its file does not. That is why it cannot collapse into `skills`,
   * and why absence has to mean "ask the file" rather than "enabled".
   *
   * `AIInterfaceModel` owns the policy, as with the role gate: it prunes an
   * entry back to absence when the requested value equals what the file already
   * says, so a later change to a shipped skill's scope is not overruled by a
   * stale stored copy.
   */
  roleSkills?: Record<string, Record<string, boolean>>;
  /**
   * Tool id → whether it belongs to the CORE always-allowed set, overriding
   * this build's `K_CORE_ALWAYS_ALLOWED_TOOL_IDS`. Ids are the registry's tool
   * names, lowercased (the tools model compares case-insensitively).
   *
   * Bidirectional like `roleSkills` and for the same reason: `true` makes a tool
   * core that the build does not ship as core, `false` withdraws one it does, and
   * ABSENCE means "whatever the build says" — never "not core", which would
   * strip every role of its meta/read tools the moment the document was written.
   *
   * A core tool is exempt from the per-role `tools:` gate, so this is the one
   * lever that changes what EVERY role can reach without editing six role files.
   * `AIInterfaceModel` owns the policy, as with the role gate: it prunes an entry
   * back to absence when the requested value equals the shipped set, so a later
   * build that adds or drops a core id is not overruled by a stale document.
   */
  coreTools?: Record<string, boolean>;
  devSettings?: AppSettingsDevSettingsDto;
  // NOTE: `promptOverrides` used to live here — whole prompt BODIES in the
  // document the boot path reads and republishes on every settings write. They
  // moved to their own per-deployment object (`models/data/agentPromptsDto.ts`)
  // when publishing became a replacement rather than a debug-gated shadow. A
  // legacy key is PRUNED on parse below rather than carried by the unknown-key
  // passthrough, so the stale bodies leave the document on the next write.
  [k: string]: unknown;
}

/** The dev-panel settings this build knows about. */
export type AppDevSettingKey = keyof Pick<
  AppSettingsDevSettingsDto,
  | "userFacingWorkflowAuthoring"
  | "respondWithVoice"
  | "hideSparkLabel"
  | "collapsibleSparkRow"
  | "superGizmo"
  | "webRtcRealtimeTransport"
>;

/**
 * What an ungated build does — the value used whenever the document says
 * nothing about a key.
 *
 * Four default TRUE because each only ever turns a SHIPPED behaviour off.
 * `superGizmo` and `hideSparkLabel` default FALSE because the former turns an
 * ALTERNATIVE on and the latter removes shipped chrome. Same split the old
 * `readPref` / `readOptInPref` pair encoded, now stated once in one table.
 *
 * ⚠ Two keys have moved from the second group to the first, both on user
 * direction: `webRtcRealtimeTransport` on 2026-08-28 (WebRTC became the shipped
 * transport) and `collapsibleSparkRow` on 2026-09-02 (the collapsible row
 * became the shipped desktop editing layout). Whenever a default moves between
 * these groups, `DevSettingsModel`'s ungated branch has to move with it — see
 * the note there.
 */
export const K_DEV_SETTING_DEFAULTS: Readonly<Record<AppDevSettingKey, boolean>> = {
  userFacingWorkflowAuthoring: true,
  respondWithVoice: true,
  hideSparkLabel: false,
  collapsibleSparkRow: true,
  superGizmo: false,
  webRtcRealtimeTransport: true,
};

/**
 * Parses the raw object. A malformed or absent document yields an EMPTY
 * settings doc — every key then falls back to its default, which is stock
 * behaviour. The distinction that matters (missing vs unreadable) is drawn one
 * layer down by `IStorageModel.getAppSettingsJson`, which returns `null` for a
 * missing object and THROWS on a transient failure; only the former reaches
 * here as `null`.
 */
export function parseAppSettings(json: string | null | undefined): AppSettingsDto {
  if (!json?.trim()) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    console.warn("[appSettings] app-settings.json is not valid JSON — treating as empty");
    return {};
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return {};

  const doc = parsed as AppSettingsDto;
  doc.skills = sanitizeEnabledMap(doc.skills);
  doc.roles = sanitizeEnabledMap(doc.roles);
  doc.roleModels = sanitizeRoleModels(doc.roleModels);
  doc.roleSkills = sanitizeRoleSkills(doc.roleSkills);
  // Same rule as the flat gates — lowercase keys, non-boolean values dropped —
  // but BOTH boolean values are meaningful here, so nothing is pruned by value.
  doc.coreTools = sanitizeEnabledMap(doc.coreTools);
  doc.devSettings = sanitizeDevSettings(doc.devSettings);
  delete doc.promptOverrides;
  return doc;
}

export function serializeAppSettings(doc: AppSettingsDto): string {
  return JSON.stringify(doc, null, 2);
}

/**
 * Drops non-boolean values and blank ids, and lowercases the keys so a
 * hand-edited document still matches the catalog id (`AIRealtimeSkillDefinition.id`,
 * `AIRealtimeRoleDefinition.id`). Shared by `skills` and `roles` — same shape,
 * same rule, so one function rather than two that could drift.
 */
function sanitizeEnabledMap(raw: unknown): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return out;
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value !== "boolean") continue;
    const id = key.trim().toLowerCase();
    if (id) out[id] = value;
  }
  return out;
}

/**
 * Keeps unknown keys (a newer build's settings) but drops non-boolean values
 * on the keys THIS build reads, so a malformed entry falls back to the default
 * instead of coercing to `false`.
 */
/**
 * Lowercases the role-id keys (so a hand-edited document still matches
 * `AIRealtimeRoleDefinition.id`), drops blank/blank-model entries, and drops a
 * `reasoningEffort` that is not one of {@link AI_REASONING_EFFORTS} — the field
 * falls back to the role's default rather than coercing a typo onto the wire.
 * Unknown keys inside an entry survive, like everywhere else in the document.
 */
function sanitizeRoleModels(raw: unknown): Record<string, AppRoleModelDto> {
  const out: Record<string, AppRoleModelDto> = {};
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return out;
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    const id = key.trim().toLowerCase();
    if (!id || !value || typeof value !== "object" || Array.isArray(value)) continue;
    const entry = { ...(value as AppRoleModelDto) };
    const model = normalizeRoleModelId(entry.model);
    if (model) entry.model = model;
    else delete entry.model;
    if (!isReasoningEffort(entry.reasoningEffort)) delete entry.reasoningEffort;
    if (Object.keys(entry).length === 0) continue;
    out[id] = entry;
  }
  return out;
}

/**
 * The per-role skill scope map: `sanitizeEnabledMap`'s rule applied at BOTH
 * levels — lowercase keys, non-boolean values dropped — plus the empty-entry
 * prune `sanitizeRoleModels` uses, so a role whose overrides were all cleared
 * leaves the document instead of persisting as `{}`.
 *
 * Deliberately an allow-list rather than `sanitizeDevSettings`' keep-everything
 * idiom: both levels are catalog IDS, not a fixed key set, so there is no
 * "unknown key from a newer build" to preserve — a key here is either an id or
 * it is noise.
 */
function sanitizeRoleSkills(raw: unknown): Record<string, Record<string, boolean>> {
  const out: Record<string, Record<string, boolean>> = {};
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return out;
  for (const [roleKey, value] of Object.entries(raw as Record<string, unknown>)) {
    const roleId = roleKey.trim().toLowerCase();
    if (!roleId) continue;
    const entry = sanitizeEnabledMap(value);
    if (Object.keys(entry).length === 0) continue;
    out[roleId] = entry;
  }
  return out;
}

function sanitizeDevSettings(raw: unknown): AppSettingsDevSettingsDto {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out = { ...(raw as AppSettingsDevSettingsDto) };
  for (const key of Object.keys(K_DEV_SETTING_DEFAULTS) as AppDevSettingKey[]) {
    if (key in out && typeof out[key] !== "boolean") delete out[key];
  }
  return out;
}
